import React, { useState, useEffect, useCallback } from 'react';
import usePageTitle from '../hooks/usePageTitle';

import { getVendors, createVendor, updateVendor, deleteVendor, getCountries } from '../utils/api';
import Modal from '../components/Modal';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  UserGroupIcon,
} from '@heroicons/react/24/outline';

const Vendors = () => {
  usePageTitle('Vendors');

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [countries, setCountries] = useState([]);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingVendor, setEditingVendor] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    company_id: '',
    address: '',
    country: '',
    vat_number: '',
  });
  const [saving, setSaving] = useState(false);

  const loadVendors = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getVendors({ search, page: pagination.page, limit: 20 });
      setVendors(response.data.vendors);
      setPagination(response.data.pagination);
    } catch (error) {
      toast.error('Error loading vendors');
    } finally {
      setLoading(false);
    }
  }, [search, pagination.page]);

  useEffect(() => {
    loadVendors();
  }, [loadVendors]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const response = await getCountries();
        setCountries(response.data);
      } catch (error) {
        console.error('Error loading countries:', error);
      }
    };
    loadCountries();
  }, []);

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const openModal = (vendor = null) => {
    if (vendor) {
      setEditingVendor(vendor);
      setFormData({
        name: vendor.name || '',
        company_id: vendor.company_id || '',
        address: vendor.address || '',
        country: vendor.country || '',
        vat_number: vendor.vat_number || '',
      });
    } else {
      setEditingVendor(null);
      setFormData({
        name: '',
        company_id: '',
        address: '',
        country: '',
        vat_number: '',
      });
    }
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingVendor(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error('Vendor name is required');
      return;
    }

    setSaving(true);
    try {
      if (editingVendor) {
        await updateVendor(editingVendor.id, formData);
        toast.success('Vendor updated successfully');
      } else {
        await createVendor(formData);
        toast.success('Vendor created successfully');
      }
      closeModal();
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error saving vendor');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendor) => {
    if (!window.confirm(`Are you sure you want to delete "${vendor.name}"?`)) {
      return;
    }

    try {
      await deleteVendor(vendor.id);
      toast.success('Vendor deleted successfully');
      loadVendors();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error deleting vendor');
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">Vendors</h1>
          <p className="page-subtitle">Manage your vendor database</p>
        </div>
        <button className="btn btn-primary" onClick={() => openModal()}>
          <PlusIcon style={{ width: 18, height: 18 }} />
          Add Vendor
        </button>
      </div>

      <div className="card">
        <div className="search-bar">
          <div className="search-input-wrapper">
            <MagnifyingGlassIcon className="search-icon" style={{ width: 18, height: 18 }} />
            <input
              type="text"
              className="form-input search-input"
              placeholder="Search vendors by name, ID, VAT, or country..."
              value={search}
              onChange={handleSearch}
            />
          </div>
        </div>

        {loading ? (
          <div className="loading-overlay">
            <div className="spinner" />
            <span>Loading vendors...</span>
          </div>
        ) : vendors.length > 0 ? (
          <>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Company ID</th>
                    <th>Name</th>
                    <th>Country</th>
                    <th>VAT Number</th>
                    <th>Address</th>
                    <th style={{ width: 100 }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {vendors.map((vendor) => (
                    <tr key={vendor.id}>
                      <td className="font-mono">{vendor.company_id}</td>
                      <td><strong>{vendor.name}</strong></td>
                      <td>{vendor.country}</td>
                      <td className="font-mono">{vendor.vat_number || '-'}</td>
                      <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {vendor.address || '-'}
                      </td>
                      <td>
                        <div className="table-actions">
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => openModal(vendor)}
                            title="Edit"
                          >
                            <PencilIcon style={{ width: 16, height: 16 }} />
                          </button>
                          <button
                            className="btn btn-ghost btn-icon btn-sm"
                            onClick={() => handleDelete(vendor)}
                            title="Delete"
                            style={{ color: 'var(--color-accent-danger)' }}
                          >
                            <TrashIcon style={{ width: 16, height: 16 }} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {pagination.totalPages > 1 && (
              <div className="pagination">
                <span className="pagination-info">
                  Page {pagination.page} of {pagination.totalPages}
                </span>
                <button
                  className="pagination-btn"
                  disabled={pagination.page === 1}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page - 1 }))}
                >
                  ‹
                </button>
                {[...Array(Math.min(5, pagination.totalPages))].map((_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      className={`pagination-btn ${pagination.page === pageNum ? 'active' : ''}`}
                      onClick={() => setPagination((prev) => ({ ...prev, page: pageNum }))}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  className="pagination-btn"
                  disabled={pagination.page === pagination.totalPages}
                  onClick={() => setPagination((prev) => ({ ...prev, page: prev.page + 1 }))}
                >
                  ›
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <UserGroupIcon className="empty-state-icon" />
            <p className="empty-state-title">No vendors found</p>
            <p>{search ? 'Try adjusting your search' : 'Add your first vendor to get started'}</p>
            {!search && (
              <button className="btn btn-primary" onClick={() => openModal()} style={{ marginTop: '1rem' }}>
                <PlusIcon style={{ width: 18, height: 18 }} />
                Add Vendor
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={modalOpen}
        onClose={closeModal}
        title={editingVendor ? 'Edit Vendor' : 'Add New Vendor'}
        footer={
          <>
            <button className="btn btn-secondary" onClick={closeModal}>
              Cancel
            </button>
            <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editingVendor ? 'Update Vendor' : 'Create Vendor'}
            </button>
          </>
        }
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Vendor Name *</label>
            <input
              type="text"
              className="form-input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter vendor name"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label className="form-label">Company ID</label>
              <input
                type="text"
                className="form-input"
                value={formData.company_id}
                onChange={(e) => setFormData({ ...formData, company_id: e.target.value })}
                placeholder="Auto-generated if empty"
              />
            </div>
            <div className="form-group">
              <label className="form-label">VAT Number</label>
              <input
                type="text"
                className="form-input"
                value={formData.vat_number}
                onChange={(e) => setFormData({ ...formData, vat_number: e.target.value })}
                placeholder="e.g., DE123456789"
              />
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Country</label>
            <select
              className="form-select"
              value={formData.country}
              onChange={(e) => setFormData({ ...formData, country: e.target.value })}
            >
              <option value="">Select country</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group mb-0">
            <label className="form-label">Address</label>
            <textarea
              className="form-textarea"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              placeholder="Enter full address"
              rows={3}
            />
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Vendors;
